import { apiFetch } from './client'
import type {
  AdminStats,
  AdminUser,
  AdminUserListResponse,
  AdminUserUpdate,
  Organization,
  OrganizationCreate,
  OrganizationListResponse,
  OrganizationUpdate,
  Practice,
  PracticeCreate,
  PracticeListResponse,
  PracticeUpdate,
} from '../types/admin'

// ── Stats ─────────────────────────────────────────────────────────────────────

export async function getAdminStats(): Promise<AdminStats> {
  const res = await apiFetch('/api/admin/stats')
  if (!res.ok) throw new Error('Failed to load stats')
  return res.json() as Promise<AdminStats>
}

// ── Users ─────────────────────────────────────────────────────────────────────

export async function listAdminUsers(params: {
  search?: string
  role?: string
  is_active?: boolean
  limit?: number
  offset?: number
}): Promise<AdminUserListResponse> {
  const qs = new URLSearchParams()
  if (params.search) qs.set('search', params.search)
  if (params.role) qs.set('role', params.role)
  if (params.is_active !== undefined) qs.set('is_active', String(params.is_active))
  if (params.limit !== undefined) qs.set('limit', String(params.limit))
  if (params.offset !== undefined) qs.set('offset', String(params.offset))
  const res = await apiFetch(`/api/admin/users?${qs.toString()}`)
  if (!res.ok) throw new Error('Failed to load users')
  return res.json() as Promise<AdminUserListResponse>
}

export async function getAdminUser(id: number): Promise<AdminUser> {
  const res = await apiFetch(`/api/admin/users/${id}`)
  if (res.status === 404) throw new Error('User not found')
  if (!res.ok) throw new Error('Failed to load user')
  return res.json() as Promise<AdminUser>
}

export async function updateAdminUser(id: number, data: AdminUserUpdate): Promise<AdminUser> {
  const res = await apiFetch(`/api/admin/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
  if (!res.ok) {
    const err = (await res.json()) as { detail?: string }
    throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to update user')
  }
  return res.json() as Promise<AdminUser>
}


// ── Organizations ──────────────────────────────────────────────────────────────

export async function listOrganizations(params: {
  search?: string
  limit?: number
  offset?: number
}): Promise<OrganizationListResponse> {
  const qs = new URLSearchParams()
  if (params.search) qs.set('search', params.search)
  if (params.limit !== undefined) qs.set('limit', String(params.limit))
  if (params.offset !== undefined) qs.set('offset', String(params.offset))
  const res = await apiFetch(`/api/admin/organizations?${qs.toString()}`)
  if (!res.ok) throw new Error('Failed to load organisations')
  return res.json() as Promise<OrganizationListResponse>
}

export async function createOrganization(data: OrganizationCreate): Promise<Organization> {
  const res = await apiFetch('/api/admin/organizations', {
    method: 'POST',
    body: JSON.stringify(data),
  })
  if (!res.ok) {
    const err = (await res.json()) as { detail?: string }
    throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to create organisation')
  }
  return res.json() as Promise<Organization>
}

export async function getOrganization(id: number): Promise<Organization> {
  const res = await apiFetch(`/api/admin/organizations/${id}`)
  if (res.status === 404) throw new Error('Organisation not found')
  if (!res.ok) throw new Error('Failed to load organisation')
  return res.json() as Promise<Organization>
}

export async function updateOrganization(id: number, data: OrganizationUpdate): Promise<Organization> {
  const res = await apiFetch(`/api/admin/organizations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
  if (!res.ok) {
    const err = (await res.json()) as { detail?: string }
    throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to update organisation')
  }
  return res.json() as Promise<Organization>
}

// ── Practices ─────────────────────────────────────────────────────────────────

export async function listPractices(params: {
  search?: string
  org_id?: number
  limit?: number
  offset?: number
}): Promise<PracticeListResponse> {
  const qs = new URLSearchParams()
  if (params.search) qs.set('search', params.search)
  if (params.org_id !== undefined) qs.set('org_id', String(params.org_id))
  if (params.limit !== undefined) qs.set('limit', String(params.limit))
  if (params.offset !== undefined) qs.set('offset', String(params.offset))
  const res = await apiFetch(`/api/admin/practices?${qs.toString()}`)
  if (!res.ok) throw new Error('Failed to load practices')
  return res.json() as Promise<PracticeListResponse>
}

export async function createPractice(data: PracticeCreate): Promise<Practice> {
  const res = await apiFetch('/api/admin/practices', {
    method: 'POST',
    body: JSON.stringify(data),
  })
  if (!res.ok) {
    const err = (await res.json()) as { detail?: string }
    throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to create practice')
  }
  return res.json() as Promise<Practice>
}

export async function getPractice(id: number): Promise<Practice> {
  const res = await apiFetch(`/api/admin/practices/${id}`)
  if (res.status === 404) throw new Error('Practice not found')
  if (!res.ok) throw new Error('Failed to load practice')
  return res.json() as Promise<Practice>
}

export async function updatePractice(id: number, data: PracticeUpdate): Promise<Practice> {
  const res = await apiFetch(`/api/admin/practices/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
  if (!res.ok) {
    const err = (await res.json()) as { detail?: string }
    throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to update practice')
  }
  return res.json() as Promise<Practice>
}
